import _ from "lodash";
import GreekDropDown from "components/Pricer/GreeksDropDown";
import {
  FIELD_NAMES,
  PRICER_COLUMNS,
  PRICER_KEYS_NOT_INCLUDED,
  PRICER_TRADER_GREEKS_STORAGE_KEY,
  PRICER_TRADER_GREEK_SLOT_COUNT,
  getDefaultTraderGreekFields,
} from "constants/pricerTable";
import { PRICER_OPT_INFO } from "constants/profile";
import {
  PRICER_EXPANDED_ROWS,
  expandedMatchedRows,
  getDataFromLocalStorage,
} from "../generalHelper";
import { SUMMARY_IDS } from "components/Pricer/FilterButtons/FilterButtons";
import {
  cellStyleFunc,
  comparatorSorter,
  disableSorterInSpecificColumns,
  editableColumnsFunc,
  getPricerGreekValueGetter,
  storeInLocalStorage,
  valueFormatterFunc,
  valueParserFunc,
} from "./Table";
import {
  buildSummaryUnderlyingUpdateBody,
  isSummaryRowUnderlyingEdit,
} from "./summaryUlEdit";
import { applyPricerColumnMenuConfig } from "./columnChooser";
import { pricerWorkingDate } from "../utcCalendarDate";
import { LightThemeStyle } from "constants/agGridCommonStyle";
import {
  applyPricerColumnSort,
  resolvePricerSortChange,
  sortedColumnsFromState,
} from "./pricerSortState";

/** Shorter bid-side headers in trader mode. String keys avoid reading FIELD_NAMES at module load (circular pricerTable ↔ GreekDropDown). */
const TRADER_BID_COLUMN_HEADERS = {
  bid_quantity: "Qty",
  bid_percentage: "%",
  bid: "Prem",
};

export const PRICER_DATAFunc = () => getDataFromLocalStorage(PRICER_OPT_INFO);
export const PRICER_SELECTED_ROWS = "pricer_selectedRows";
export const PRICER_FILTER_BUTTON_SELECTED = "pricer_filterButtonSelected";
export const PRICER_DATE_SELECTED = "pricer_dateSelected";
export const PRICER_SORTED_COLUMNS = "pricer_sortedColumns";

export const selectRowBasedOnSummaryId = (gridAPI) => {
  const pricerData = PRICER_DATAFunc();
  if (!pricerData[PRICER_SELECTED_ROWS]?.length) return;

  pricerData[PRICER_SELECTED_ROWS].forEach((summaryId) => {
    gridAPI?.forEachNode((node) => {
      const nodeData = node.group ? node.aggData : node.data;
      if (nodeData[FIELD_NAMES.SUMMARY_ID] === summaryId) {
        node.setSelected(true);
      }
    });
  });
};

export const expandRowBasedOnSummaryId = (gridAPI) => {
  const pricerData = PRICER_DATAFunc();

  if (_.isEmpty(pricerData?.[PRICER_EXPANDED_ROWS])) return;

  Object.values(pricerData[PRICER_EXPANDED_ROWS]).forEach((keyItem) => {
    expandedMatchedRows(gridAPI, keyItem);
  });
};

/**
 * Exports the data from the Pricer grid as a CSV file.
 * @param {Object} gridRef - A reference to the Pricer grid component.
 */
export const onPricerBtnExport = (gridRef) => {
  let rows = [];
  gridRef.current.api.forEachNode((node) => {
    // Create a deep copy of originalRowData
    const originalRowData = node.group ? node.aggData : node.data;
    const rowData = JSON.parse(JSON.stringify(originalRowData));
    // Add this debug log

    PRICER_KEYS_NOT_INCLUDED.forEach((key) => delete rowData[key]);
    rows.push(rowData);
  });

  const options = rows.reduce(function (rv, x) {
    (rv[x["summary_id"]] = rv[x["summary_id"]] || []).push(x);
    return rv;
  }, {});

  let csvContent = "data:text/csv;charset=utf-8,";

  Object.values(options).forEach((option, key) => {
    if (key === 0) {
      const columns = Object.keys(rows[0]);
      csvContent += columns.map((col) => `"${col}"`).join(",") + "\r\n"; // Escaping column names
    }

    option.forEach(function (rowObject) {
      let rowArray = Object.keys(rows[0]).map((col) => rowObject[col] || ""); // Consistent column order
      let row = rowArray
        .map((item) => `"${item.toString().replace(/"/g, '""')}"`) // Escape special characters
        .join(",");
      csvContent += row + "\r\n";
    });

    const emptyRow = Array.from(rows[0])
      .map(() => "")
      .join(",");
    csvContent += emptyRow + "\r\n";
  });

  var encodedUri = encodeURI(csvContent);
  window.open(encodedUri);
};

/**
 * Flashes the cells in the Pricer table that have new summary IDs.
 * @param {Object} gridRef - The reference to the Pricer table grid.
 * @param {Array} pricerTablesData - The data of the Pricer tables.
 */
export const onPricerCellFlash = (gridRef, pricerTablesData) => {
  const savedSummaryIds = getDataFromLocalStorage(SUMMARY_IDS);
  if (!savedSummaryIds) return;
  const currentSummaryIds =
    pricerTablesData && pricerTablesData?.map((item) => item.summary_id);
  const newSummaryId =
    currentSummaryIds &&
    currentSummaryIds?.filter((item) => !savedSummaryIds.includes(item));
  let rowsWithNewSummaryId = [];
  newSummaryId.length &&
    newSummaryId?.forEach((id) => {
      gridRef.current?.api?.forEachNode((node) => {
        const nodeData = node.group ? node.aggData : node.data;
        if (!nodeData) return;
        if (nodeData.summary_id === id) rowsWithNewSummaryId.push(node);
      });
    });
  if (!rowsWithNewSummaryId || rowsWithNewSummaryId.length === 0) return;
  setTimeout(() => {
    gridRef.current?.api.flashCells({ rowNodes: rowsWithNewSummaryId });
  }, 500);
  storeSummaryIdsInLocalStorage(pricerTablesData);
};

export const storeSummaryIdsInLocalStorage = (pricerTablesData) => {
  const summaryIds = pricerTablesData?.map((item) => item.summary_id);
  storeInLocalStorage(SUMMARY_IDS, JSON.stringify(summaryIds));
};

function buildTraderGreekSlotColumnDefs() {
  const saved = getDataFromLocalStorage(PRICER_TRADER_GREEKS_STORAGE_KEY) || {};
  const defaults = getDefaultTraderGreekFields();
  return Array.from({ length: PRICER_TRADER_GREEK_SLOT_COUNT }, (_, slotIndex) => {
    const key = String(slotIndex);
    const field = saved[key] || defaults[slotIndex];
    const base = {
      minWidth: 120,
      flex: 1,
      colId: `trader_greek_slot_${slotIndex}`,
      field,
      cellClass: "greek-column-cell",
      headerComponent: GreekDropDown,
      headerComponentParams: { traderGreekSlotIndex: slotIndex },
    };
    return {
      ...base,
      menuTabs: [],
      resizable: true,
      sortable: disableSorterInSpecificColumns(base),
      valueGetter: getPricerGreekValueGetter,
      valueFormatter: valueFormatterFunc,
      valueParser: valueParserFunc,
      comparator: comparatorSorter(base),
    };
  });
}

function mergePricerGreekColumns(rawColumnDefs, notTrader) {
  const firstGreekIdx = rawColumnDefs.findIndex(
    (c) => c.headerComponent === GreekDropDown
  );
  if (firstGreekIdx === -1) return rawColumnDefs;

  let greekEnd = firstGreekIdx;
  while (
    greekEnd < rawColumnDefs.length &&
    rawColumnDefs[greekEnd]?.headerComponent === GreekDropDown
  ) {
    greekEnd += 1;
  }

  const head = rawColumnDefs.slice(0, firstGreekIdx);
  const tail = rawColumnDefs.slice(greekEnd);
  const greekCols = notTrader
    ? rawColumnDefs.slice(firstGreekIdx, greekEnd)
    : buildTraderGreekSlotColumnDefs();

  return [...head, ...greekCols, ...tail];
}

export const pricerColumnsData = (
  dark_Theme,
  editable,
  rowEditingData,
  secondary_companies_est_columns,
  notTrader,
  est_columns
) => {
  const initial = getDataFromLocalStorage("greeksDropDown");
  const columnWidthMap = getDataFromLocalStorage(PRICER_OPT_INFO)?.columnState;
  const raw = PRICER_COLUMNS(
    initial,
    secondary_companies_est_columns,
    columnWidthMap,
    est_columns
  );
  const merged = mergePricerGreekColumns(raw, notTrader);
  return merged.map((item) => {
    const hideUpdateColumn =
      item.field === FIELD_NAMES.IS_AUTO_UPDATE_VOL && notTrader;

    const askColumnsToHideForNonTraders = [
      FIELD_NAMES.ASK_PERCENTAGE,
      FIELD_NAMES.ASK,
      FIELD_NAMES.ASK_QTY,
      FIELD_NAMES.ASK_DELTA,
      FIELD_NAMES.ASK_GAMMA,
      FIELD_NAMES.ASK_VEGA,
      FIELD_NAMES.ASK_RHO,
      FIELD_NAMES.ASK_THETA,
    ];
    const hideAskColsTrader =
      !notTrader && askColumnsToHideForNonTraders.includes(item.field);

    const traderBidHeaderOverride =
      !notTrader &&
      item.field &&
      TRADER_BID_COLUMN_HEADERS[item.field];

    const roleHidden = hideUpdateColumn || hideAskColsTrader;
    const withMenu = applyPricerColumnMenuConfig({
      ...item,
      ...(traderBidHeaderOverride
        ? { headerName: traderBidHeaderOverride }
        : {}),
      hide: roleHidden ? true : item.hide,
    });

    if (roleHidden) {
      withMenu.suppressColumnsToolPanel = true;
      withMenu.lockVisible = true;
    }

    return {
      ...withMenu,
      editable: (event) => editableColumnsFunc(event, editable, rowEditingData),
      cellStyle: (params) => {
        if (!dark_Theme) {
          return LightThemeStyle;
        } else {
          return cellStyleFunc(params);
        }
      },
    };
  });
};

export const setPricerModalSort = (params, stateToSort) => {
  applyPricerColumnSort(params.columnApi, stateToSort);
};

export { reapplyPricerSort } from "./pricerSortState";

/**
 * A function that retrieves the row ID from the given parameters.
 * @param {object} params - the parameters containing the data and node
 * @return {string} the ID of the row
 */
export const getPricerRowId = (params) => {
  if (!params.data) {
    return params.node.id;
  }
  return params.data.id;
};

export const onSaveInGlobalContext = (gridApi, setPricerOperation) => {
  const pricerDataFromLocalStorage = (() => PRICER_DATAFunc())();
  const selectedSummaryId = gridApi
    .getBestCostNodeSelection()
    .map((item) =>
      item?.group
        ? item.aggData?.[FIELD_NAMES.SUMMARY_ID]
        : item?.data?.[FIELD_NAMES.SUMMARY_ID]
    );
  setPricerOperation({
    ...pricerDataFromLocalStorage,
    [PRICER_SELECTED_ROWS]: selectedSummaryId?.length
      ? selectedSummaryId
      : null,
  });
};

export const onPricerSortChanged = (params, setPricerOperation) => {
  const columnState = params.columnApi.getColumnState();
  const nextSort = resolvePricerSortChange(columnState);
  if (nextSort !== null) {
    applyPricerColumnSort(params.columnApi, nextSort);
    return;
  }

  const sortedColumns = sortedColumnsFromState(columnState);
  const pricerDataFromLocalStorage = (() => PRICER_DATAFunc())();
  setPricerOperation({
    ...pricerDataFromLocalStorage,
    [PRICER_SORTED_COLUMNS]: sortedColumns,
  });
};

export { isSummaryRowUnderlyingEdit, resolvePricerSummaryId } from "./summaryUlEdit";

export const pricerPrepareDataForCellEditing = (
  rowData,
  event,
  dateSelected
) => {
  const calculationDate = pricerWorkingDate(dateSelected);
  if (isSummaryRowUnderlyingEdit(event)) {
    return buildSummaryUnderlyingUpdateBody(rowData, event, calculationDate);
  }

  const isTenor = event.colDef.field === "tenor";
  const prepareBody = {
    [FIELD_NAMES.ROW_TYPE]: rowData.row_type,
    [FIELD_NAMES.LEG_ID]: rowData[FIELD_NAMES.LEG_ID],
    [FIELD_NAMES.SUMMARY_ID]: rowData[FIELD_NAMES.SUMMARY_ID],
    [FIELD_NAMES.OPTION_ROW_ID]: rowData.id,
    [FIELD_NAMES.CALCULATION_DATE]: calculationDate,
    [isTenor ? "style_period_tenor_id" : event.colDef.field]: isTenor
      ? +(event.data?.style_period_tenor_id ?? rowData?.style_period_tenor_id)
      : +event.newValue,
  };

  return prepareBody;
};

export const onPricerCellEditingStarted = (
  event,
  setRowEditingData,
  dark_Theme
) => {
  const node = event.node;
  const rowData = node.group ? node.aggData : node.data;
  var editor = event.api.getCellEditorInstances({
    rowIndex: event.rowIndex,
    column: event.column,
  });
  if (editor && editor.length > 0) {
    const gui = editor[0].getGui();
    const input = gui?.querySelector?.("input");
    if (dark_Theme) {
      gui.style.color = "var(--color-white-1, #fff)";
      gui.style.backgroundColor = "var(--color-black-1, #141414)";
      if (input) {
        input.style.color = "var(--color-white-1, #fff)";
        input.style.backgroundColor = "var(--color-black-1, #141414)";
        input.style.caretColor = "var(--color-white-1, #fff)";
      }
    } else {
      gui.style.color = "var(--color-black-1, #000)";
      if (input) {
        input.style.color = "var(--color-black-1, #000)";
        input.style.backgroundColor = "var(--color-white-1, #fff)";
      }
    }
  }
  setRowEditingData(rowData);
  // setIsCellEditing(true);
};
